import {isAiBundle, bundleMetadata} from '@lib/messarioAi/storage';
import {fetchMessarioAiGenerateBundle, MessarioAiFetchFailure} from '@lib/messarioAi/api';
import {
  MessarioAiBundleEnabled,
  MessarioAiGenerateBundleEnabled
} from '@lib/messarioAi/types';

export const MESSARIO_AI_GENERATE_BUNDLE_KEY_PREFIX = 'messario_ai_generate_bundle:';

function storageKey(externalUserId: string) {
  return `${MESSARIO_AI_GENERATE_BUNDLE_KEY_PREFIX}${externalUserId}`;
}

function isGenerateBundleEnabled(value: unknown): value is MessarioAiGenerateBundleEnabled {
  return isAiBundle(value) && value.enabled;
}

export function generateBundleMatchesMetadata(cached: MessarioAiGenerateBundleEnabled, metadata: MessarioAiBundleEnabled): boolean {
  return cached.config_schema_version === metadata.config_schema_version && cached.prompt_revision === metadata.prompt_revision && cached.variable_definitions_revision === metadata.variable_definitions_revision && cached.config_hash === metadata.config_hash;
}

export function getMessarioAiGenerateBundleCache(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): MessarioAiGenerateBundleEnabled | undefined {
  const value = storage.getItem(storageKey(externalUserId));
  if(!value) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if(!isGenerateBundleEnabled(parsed)) {
      storage.removeItem(storageKey(externalUserId));
      return undefined;
    }

    const cleaned = bundleMetadata(parsed) as MessarioAiGenerateBundleEnabled;
    const encoded = JSON.stringify(cleaned);
    if(encoded !== value) {
      storage.setItem(storageKey(externalUserId), encoded);
    }

    return cleaned;
  } catch{
    storage.removeItem(storageKey(externalUserId));
    return undefined;
  }
}

export function saveMessarioAiGenerateBundleCache(
  externalUserId: string,
  bundle: MessarioAiGenerateBundleEnabled,
  storage: Storage = window.sessionStorage
) {
  if(!externalUserId || !isGenerateBundleEnabled(bundle)) {
    return;
  }

  storage.setItem(
    storageKey(externalUserId),
    JSON.stringify(bundleMetadata(bundle))
  );
}

export function clearMessarioAiGenerateBundleCache(
  externalUserId: string,
  storage: Storage = window.sessionStorage
) {
  storage.removeItem(storageKey(externalUserId));
}

export async function ensureMessarioAiGenerateBundle(
  token: string,
  panelOrigin: string,
  externalUserId: string,
  metadata: MessarioAiBundleEnabled,
  fetchValue: typeof fetch = fetch,
  storage: Storage = window.sessionStorage,
  forceRefresh = false
): Promise<MessarioAiGenerateBundleEnabled> {
  if(!forceRefresh) {
    const cached = getMessarioAiGenerateBundleCache(externalUserId, storage);
    if(cached && generateBundleMatchesMetadata(cached, metadata)) {
      return cached;
    }
  }

  const fetched = await fetchMessarioAiGenerateBundle(token, panelOrigin, fetchValue);
  if(!fetched.enabled) {
    clearMessarioAiGenerateBundleCache(externalUserId, storage);
    throw new MessarioAiFetchFailure(
      'configuration',
      'AI-менеджер выключен. Обновите страницу после настройки в Panel.'
    );
  }

  saveMessarioAiGenerateBundleCache(externalUserId, fetched, storage);
  return fetched;
}
